import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DccConfigurationFeatureFlag } from '../../models/dcc-configuration.model';
import { DccConfigurationService } from '../dcc-configuration/dcc-configuration.service';

@Injectable({
  providedIn: 'root',
})
export class DccFeatureAccessService {
  private readonly dccConfigurationService = inject(DccConfigurationService);
  private readonly featureFlagsState = signal<
    DccConfigurationFeatureFlag[] | null
  >(null);
  private loadPromise?: Promise<DccConfigurationFeatureFlag[]>;

  readonly featureFlags = computed(() => this.featureFlagsState() ?? []);

  async ensureLoaded(): Promise<DccConfigurationFeatureFlag[]> {
    const cachedFlags = this.featureFlagsState();
    if (cachedFlags) {
      return cachedFlags;
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = firstValueFrom(
      this.dccConfigurationService.getFeatureFlags(),
    ).then((featureFlags) => {
      this.featureFlagsState.set(featureFlags);
      return featureFlags;
    });

    try {
      return await this.loadPromise;
    } finally {
      this.loadPromise = undefined;
    }
  }

  hasAccess(featureName?: string, dccCode?: string | null): boolean {
    if (!featureName) {
      return true;
    }

    const featureFlag = this.findFeatureFlag(featureName);
    if (!featureFlag) {
      return true;
    }

    if (!featureFlag.enabled) {
      return false;
    }

    const normalizedDccCode = this.normalizeValue(dccCode);
    if (!normalizedDccCode) {
      return false;
    }

    const assignedGroups = (featureFlag.groups ?? [])
      .map((group) => this.normalizeValue(group.name))
      .filter((group): group is string => group.length > 0);

    if (assignedGroups.length === 0) {
      return false;
    }

    return assignedGroups.includes(normalizedDccCode);
  }

  async canAccess(
    featureName?: string,
    dccCode?: string | null,
  ): Promise<boolean> {
    if (!featureName) {
      return true;
    }

    await this.ensureLoaded();
    return this.hasAccess(featureName, dccCode);
  }

  private findFeatureFlag(
    featureName: string,
  ): DccConfigurationFeatureFlag | undefined {
    const normalizedFeatureName = this.normalizeValue(featureName);

    return this.featureFlags().find(
      (featureFlag) =>
        this.normalizeValue(featureFlag.name) === normalizedFeatureName,
    );
  }

  private normalizeValue(value?: string | null): string {
    return value?.trim().toLowerCase() ?? '';
  }
}
